import type { Role } from '@mend/types/user';

/**
 * Kept free of app imports (router, queries, analytics) so it can be used by
 * the token bootstrap, which must run before those modules are evaluated.
 */
interface MendJWT {
  aud: Role;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  masterOrgId?: string;
  nbf: number;
  org?: string;
  sub: number;
}

function parseMendJwt(token: string): MendJWT | null {
  try {
    const base64Url = token.split('.')[1];

    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = window.atob(base64);

    return JSON.parse(jsonPayload) as MendJWT;
  } catch {
    return null;
  }
}

/**
 * Check if the token is valid.
 */
export function isTokenValid(token: string | undefined): boolean {
  if (!token) return false;

  const parsedToken = parseMendJwt(token);
  if (!parsedToken) return false;

  /**
   * The token's expiration comes in seconds, so we need to convert the current
   * time first.
   */
  const currentTime = Date.now() / 1_000;
  return parsedToken.exp > currentTime;
}
